import { useCallback, useContext } from "react";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../state-machine/reviewBoardFlowLoader";
import { useAppDispatch } from "../../../store";
import {
  BoardMachinesStates,
  boardNotFound,
  boardRevisionLoaded,
  BoardRevisions,
  pageFailure,
} from "../../../../../reducers/uploadedBoard";
import settings from "../../../../../constants/settings";
import useFetchAndTrace from "../../../fetchWithTrace";

export default function useRefreshBoardRevision() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const fetchAndTrace = useFetchAndTrace();

  const handleFetch = useCallback(
    async (response: Response) => {
      if (response.ok) {
        const boardRevisions = (await response.json()) as BoardRevisions;
        dispatch(boardRevisionLoaded({ boardId, boardRevisions }));
      } else if (response.status == 404 && boardId) {
        dispatch(boardNotFound({ boardId }));
      } else {
        throw Error("Failed to fetch board revisions");
      }
    },
    [boardId, dispatch],
  );

  const handleFetchError = useCallback(() => {
    if (boardId) {
      dispatch(pageFailure({ boardId }));
    }
  }, [dispatch, boardId]);

  const refreshBoardRevision = useCallback(() => {
    void fetchAndTrace(
      `${settings.api_url}/boards/${boardId}/revisions`,
      handleFetch,
      handleFetchError,
    );
  }, [boardId, fetchAndTrace, handleFetch, handleFetchError]);

  const handleBoardRevisionStateUpdates = useCallback(
    (nextStatesData: BoardMachinesStates, states: BoardMachinesStates) => {
      if (states.board_revision_state != nextStatesData.board_revision_state) {
        refreshBoardRevision();
      }
    },
    [refreshBoardRevision],
  );

  return {
    handleBoardRevisionStateUpdates,
    refreshBoardRevision,
  };
}
